import React, { forwardRef, useImperativeHandle, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useParams, useSearchParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import { getBenefitsData, saveBenefitsForm } from '../../../../../../api/transitions';
import { trimData } from '../../../../../../utils/formatText';
import SkeletonBlock from '../../../SkeletonBlock';
import { BENEFITS } from '../../../formQuestionsEnum';
import { enqueueSnackbar } from '../../../../../../reusable-components/snackbar';
import BenefitInformationForm from './BenefitInformationForm';

const BenefitsView = forwardRef(({ setFormData }, ref) => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const facilityId = searchParams.get('facilityId');
  const [data, setData] = useState();
  const [isLoading, setIsLoading] = useState(true);
  const userPermissions = useSelector((state) => state.transitions?.data?.userPermissions);

  const benefitsPermissions = Object.fromEntries(
    Object.values(userPermissions?.[id]?.[facilityId]?.benefitsFieldPermissions || {}).map((x) => [
      x.formQuestionId,
      x.isEditable,
    ])
  );

  useEffect(() => {
    getBenefitsData(id, facilityId)
      .then((resp) => {
        setData(resp.data);
      })
      .catch(() => {
        enqueueSnackbar('Failed to load data', { variant: 'error' });
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [id, facilityId]);

  useEffect(() => {
    const fieldNameMap = Object.fromEntries(Object.entries(BENEFITS).map(([, v]) => [v.id, v.fieldName]));

    const mappedFields = {
      ...data?.fields?.reduce((acc, field) => ({ ...acc, [fieldNameMap[field.formQuestionId]]: field.answer }), {}),
    };

    setFormData({ transitionFacilityId: data?.transitionFacilityId, ...mappedFields });
  }, [data]);

  const submitForm = async (allData) => {
    const result = trimData(allData);

    await saveBenefitsForm({
      id,
      sellerFacilityId: facilityId,
      ...result,
    })
      .then((resp) => {
        enqueueSnackbar('Benefits data saved successfully', { variant: 'success' });
        setData(resp.data);
      })
      .catch(() => {
        enqueueSnackbar('Failed to save Benefits data', { variant: 'error' });
      });
  };

  useImperativeHandle(ref, () => ({ submitForm }));

  return isLoading ? (
    <SkeletonBlock height={600} sx={{ mt: 2 }} />
  ) : (
    <BenefitInformationForm permissions={benefitsPermissions} />
  );
});

export default BenefitsView;

BenefitsView.propTypes = {
  setFormData: PropTypes.func,
};
